import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Phone, ChevronDown, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { LANGS, SERVICES, STATIC_PATHS, langFromPath, servicePath, translatePath } from "@/lib/services";
import { ui } from "@/lib/siteText";
import { SITE_CONFIG } from "@/lib/constants";
import { useActiveServiceKeys } from "@/hooks/useServicePlaces";
import { useNavigation } from "@/hooks/useNavigation";

const telHref = `tel:${SITE_CONFIG.phone.replace(/\s/g, "")}`;

const Header = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const lang = langFromPath(pathname);
  const t = ui(lang);
  const { data: active } = useActiveServiceKeys();
  const services = SERVICES.filter((s) => !active || active.has(s.key));
  const { data: navigation } = useNavigation();
  const customNav = navigation?.filter(n => n.href.startsWith("/") && !n.href.startsWith("//") && !n.href.startsWith("/admin"));

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/90 backdrop-blur">
      <div className="container flex h-16 items-center justify-between gap-4">
        <Link to={STATIC_PATHS.home[lang]} className="flex items-center gap-2 font-display text-xl font-bold tracking-tight">
          <span className="inline-block h-2.5 w-2.5 rounded-sm bg-primary" />
          CYSCOM
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="gap-1">{t.services} <ChevronDown className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-72">
              {services.map((s) => (
                <DropdownMenuItem key={s.key} asChild>
                  <Link to={servicePath(s.key, lang)} className="flex items-center gap-2">
                    <s.icon className="h-4 w-4 text-primary" /> {s.name[lang]}
                  </Link>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          {customNav?.length ? customNav.map(n => <Button asChild variant="ghost" key={n.id}><Link to={n.href}>{n.name}</Link></Button>) : <><Button asChild variant="ghost"><Link to={STATIC_PATHS.about[lang]}>{t.about}</Link></Button><Button asChild variant="ghost"><Link to={STATIC_PATHS.contact[lang]}>{t.contact}</Link></Button></>}
        </nav>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-1 font-mono uppercase" aria-label="Language">
                <Globe className="h-4 w-4" /> {lang}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {LANGS.map((l) => (
                <DropdownMenuItem key={l} onClick={() => { localStorage.setItem("language", l); navigate(translatePath(pathname, l)); }} className="font-mono uppercase">
                  {l}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <Button asChild variant="outline" size="sm" className="hidden md:inline-flex">
            <a href={telHref}><Phone className="mr-2 h-4 w-4" />{SITE_CONFIG.phone}</a>
          </Button>
          <Button asChild size="sm" className="hidden sm:inline-flex">
            <Link to={servicePath("website-gehackt", lang)}>{t.urgent}</Link>
          </Button>
          <Button variant="ghost" size="icon" className="lg:hidden" onClick={() => setOpen(!open)} aria-label="Menu">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
        </div>
      </div>

      {open && (
        <div className="border-t border-border lg:hidden">
          <nav className="container flex flex-col py-4">
            {services.map((s) => (
              <Link key={s.key} to={servicePath(s.key, lang)} className="flex items-center gap-2 py-2">
                <s.icon className="h-4 w-4 text-primary" /> {s.name[lang]}
              </Link>
            ))}
            {customNav?.length ? customNav.map(n => <Link key={n.id} to={n.href} className="py-2">{n.name}</Link>) : <><Link to={STATIC_PATHS.about[lang]} className="py-2">{t.about}</Link><Link to={STATIC_PATHS.contact[lang]} className="py-2">{t.contact}</Link></>}
            <a href={telHref} className="py-2 font-medium text-primary">{SITE_CONFIG.phone}</a>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
